import type { SerializeFrom } from '@remix-run/node'
import type { useFetcher } from '@remix-run/react'
import { CircleAlert } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { PublicCartPriceDetail } from '~/helpers/cart'
import { Skeleton, Tooltip } from '~/ui'
import { cn } from '~/utils/classnames'
import { isNumber } from '~/utils/number'

import { Price } from '../price'

interface CartPriceDetailsProps {
  className?: string
  priceDetail: SerializeFrom<PublicCartPriceDetail> | null
  fetcher: ReturnType<typeof useFetcher>
}

export const CartPriceDetails = ({
  priceDetail,
  fetcher,
  className,
}: CartPriceDetailsProps) => {
  const { t } = useTranslation()

  const subtotal = useMemo(() => {
    if (!priceDetail) return { current: null, previous: null }
    const { subtotalAmount, subtotalWithoutDiscount } = priceDetail

    return {
      current: subtotalAmount,
      previous:
        subtotalWithoutDiscount != subtotalAmount
          ? subtotalWithoutDiscount
          : null,
    }
  }, [priceDetail])

  const shipping = useMemo(() => {
    if (!priceDetail) return null

    return priceDetail.shippingAmount
  }, [priceDetail])

  const isLoading = fetcher.state !== 'idle'

  return (
    <ol
      className={cn(
        'space-y-1.5 text-sm font-medium *:flex *:items-center *:justify-between *:gap-2',
        className
      )}
    >
      <li>
        {t('components.cart.labels.subtotal')}
        <ConditionalSkeleton className="space-x-1" isActive={isLoading}>
          {subtotal.previous && (
            <Price amount={subtotal.previous} variant="strikethrough" />
          )}
          {isNumber(subtotal.current) && <Price amount={subtotal.current} />}
          {!isNumber(subtotal.current) && <span>-</span>}
        </ConditionalSkeleton>
      </li>
      <li>
        <span className="flex items-center gap-1.5">
          {t('components.cart.labels.estimated_shipping')}
          <Tooltip>
            <Tooltip.Trigger>
              <CircleAlert className="size-4.5" />
            </Tooltip.Trigger>
            <Tooltip.Content className="text-center">
              {t('components.cart.info.estimated_shipping')}
            </Tooltip.Content>
          </Tooltip>
        </span>
        <ConditionalSkeleton className="space-x-1" isActive={isLoading}>
          {isNumber(shipping) && <Price amount={shipping} />}
          {!isNumber(shipping) && <span>-</span>}
        </ConditionalSkeleton>
      </li>
      <li>
        {t('components.cart.labels.total')}
        <ConditionalSkeleton isActive={isLoading}>
          {isNumber(priceDetail?.totalAmount) && (
            <Price amount={priceDetail.totalAmount} />
          )}
          {!isNumber(priceDetail?.totalAmount) && <span>-</span>}
        </ConditionalSkeleton>
      </li>
    </ol>
  )
}

interface ConditionalSkeletonProps {
  isActive?: boolean
  children: React.ReactNode
  className?: string
}

const ConditionalSkeleton = ({
  isActive = true,
  className,
  children,
}: ConditionalSkeletonProps) => {
  return (
    <div className={cn('relative', className)}>
      {isActive && (
        <div className="bg-background absolute -inset-x-0.5 inset-y-0 z-10 m-0!">
          <Skeleton className="absolute inset-0 rounded-md" />
        </div>
      )}
      {children}
    </div>
  )
}
